import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API_BASE from "../config";

// ── ProjectDetailPage ──────────────────────────────────────────────────
// Project-level overview that shows every Calculation belonging to one
// project as a sortable comparison table (default) or a card grid.
// Implements PRD_followups.md §4/W3 (project / calculation decoupling)
// plus §6/W5 (the UI side of the W3 schema).
//
// Each calculation surfaces: variant_label, created_at, best_fitness,
// the EN 12193 trio (Ē / U / RG) + total_wattage from the linked Result,
// and the "Empfehlung" badge if is_recommended=TRUE. Actions per row:
//   * Report → POST /printreport/:id?calculation_id=N (opens SPA in tab)
//   * ★ promote → POST /projects/:id/calculations/:cid/recommend
//   * ✕ delete  → DELETE /calculations/:cid (refused for the recommended)
//
// 2026-05-20 redesign: every visual element uses the design-system
// utility classes in App.css (`.data-table`, `.kpi-card`,
// `.badge-recommend`, `.view-toggle`, `.pill`, `.toast`); only the
// page-grid wrapping is inline.
//
// Props:
//   projectId — optional, only when mounted as the legacy Topbar overlay
//   onBack    — optional, only when mounted as the legacy Topbar overlay
// ───────────────────────────────────────────────────────────────────────
// Label der AKTUELLEN Pipeline (Philips Wunsch 26.08.2026): Läufe des neuen
// Algorithmus sollen in Tabelle und Karten auch dann farblich hervorstechen,
// wenn sie „nicht bestanden" sind — sonst gehen sie zwischen den alten
// june_pipeline-Zeilen unter. Dunkles Signify-Grün, lesbar auch auf den
// gedimmten Fail-Zeilen. Bei der nächsten Pipeline-Generation hier umziehen.
const AKTUELLE_PIPELINE = "late_august_pipeline";
const pipelineLabelStyle = (label) =>
  label === AKTUELLE_PIPELINE ? { color: "#047857" } : undefined;

const ProjectDetailPage = ({ projectId: projectIdProp, onBack: onBackProp }) => {
  const params = useParams();
  const navigate = useNavigate();
  const projectId = projectIdProp ?? Number(params.id);
  const onBack = onBackProp ?? (() => navigate("/projects"));

  const [project, setProject] = useState(null);
  const [calculations, setCalculations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  // View mode: table for side-by-side comparison, cards for browsing.
  const [view, setView] = useState("table");

  // Default sort: U (Gleichmaessigkeit) desc — das ist auch das Kriterium der
  // Empfehlung. Bis 21.09.2026 stand hier best_fitness; seit dem Aug-26-Drop
  // melden geloeste Laeufe aber alle denselben Sentinel (1e6), damit war die
  // Sortierung bedeutungslos. RG defaults to asc (lower glare = better).
  const [sort, setSort] = useState({ key: "pa_u", dir: "desc" });

  // QW-2: failed variants are tucked into a collapsed section (default off).
  const [showFailed, setShowFailed] = useState(false);

  const showToast = (msg, ms = 3000) => {
    setToast(msg);
    setTimeout(() => setToast(null), ms);
  };

  // Pull project + calculations together so cards/table reflect server state.
  const reload = async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const [pRes, cRes] = await Promise.all([
        fetch(`${API_BASE}/projects/${projectId}`),
        fetch(`${API_BASE}/projects/${projectId}/calculations`),
      ]);
      if (!pRes.ok) throw new Error(`Projekt: HTTP ${pRes.status}`);
      if (!cRes.ok) throw new Error(`Berechnungen: HTTP ${cRes.status}`);
      const pData = await pRes.json();
      const cData = await cRes.json();
      setProject(pData);
      setCalculations(Array.isArray(cData) ? cData : []);
    } catch (e) {
      console.error("Failed to load project detail", e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Promote a calc — backend handles demoting the previously-recommended one.
  const handleRecommend = async (calc) => {
    try {
      const res = await fetch(
        `${API_BASE}/projects/${projectId}/calculations/${calc.id}/recommend`,
        { method: "POST" }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await reload();
      showToast(`„${calc.variant_label}" als Empfehlung gesetzt.`);
    } catch (e) {
      console.error("Recommend failed", e);
      showToast(`Setzen der Empfehlung fehlgeschlagen: ${e.message}`);
    }
  };

  const handleDelete = async (calc) => {
    if (!window.confirm(
      `Berechnung „${calc.variant_label}" (id=${calc.id}) wirklich löschen?`
    )) return;
    try {
      const res = await fetch(`${API_BASE}/calculations/${calc.id}`, { method: "DELETE" });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || `HTTP ${res.status}`);
      }
      await reload();
      showToast("Berechnung gelöscht.");
    } catch (e) {
      console.error("Delete failed", e);
      showToast(`Löschen fehlgeschlagen: ${e.message}`);
    }
  };

  // Open the printable report for a specific Calculation. POSTs to
  // /printreport with ?calculation_id=N so the lightmap-api gets the
  // variant's metrics, not the recommended-by-default one.
  //
  // WICHTIG: Das Tab wird SOFORT im Klick-Kontext geöffnet und erst nach
  // der Antwort auf die Report-URL umgeleitet. Die LightMap-API braucht
  // kalt bis zu 60 s — ein window.open() NACH dem fetch gilt für den
  // Browser nicht mehr als Nutzeraktion und wird vom Popup-Blocker
  // stillschweigend geschluckt ("Report-Button tut nichts", 2026-07-03).
  const openReport = async (calcId) => {
    const tab = window.open("", "_blank");
    if (tab) {
      tab.document.title = "Report wird erstellt…";
      tab.document.body.innerHTML =
        "<p style='font-family:sans-serif;padding:2rem;color:#334155'>" +
        "Report wird erstellt – das kann bis zu einer Minute dauern…</p>";
    }
    try {
      const res = await fetch(
        `${API_BASE}/printreport/${projectId}?calculation_id=${calcId}`,
        { method: "POST" }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!(data && data.url)) throw new Error("Report-Response ohne URL.");
      if (tab && !tab.closed) tab.location = data.url;
      else window.open(data.url, "_blank"); // Fallback, falls das Tab doch geblockt/geschlossen wurde
    } catch (e) {
      if (tab && !tab.closed) tab.close();
      console.error("Report failed", e);
      showToast(`Report fehlgeschlagen: ${e.message}`);
    }
  };

  // ── „Skizze bearbeiten" (07.08.2026, Signify) ───────────────────────
  // Öffnet den Zeichner mit der GESPEICHERTEN Skizze und springt dort
  // direkt an den Schritt vor „Kalkulation starten": Feld, Masten,
  // Fassaden und Lichtfarbe stehen schon da, man korrigiert nur das
  // Nötige und der Submit legt eine WEITERE Variante an.
  //
  // Bewusst EIN Knopf im Kopf statt einer je Tabellenzeile: die
  // Geometrie ist projekt- und nicht variantenweit gespeichert
  // (PRD_followups §4/W3 — Lichtpunkte hängen immer am jüngsten Lauf),
  // es gibt also nur EINE Skizze — die des letzten Laufs. Ein
  // Zeilen-Knopf würde eine Auswahl vorgaukeln, die es nicht gibt.
  const openEditor = () => {
    navigate(`/projects/${projectId}/sketch`, { state: { resumeSketch: true } });
  };

  // ── „Neu berechnen" ─────────────────────────────────────────────────
  // Rechnet ohne Zeichnen: POST /reoptimize/:id nimmt die gespeicherte
  // Geometrie und lässt den GA erneut darüber laufen — über dieselbe
  // Kette (optimisation_pipeline) wie der Submit im Zeichner, seit dem
  // Ausbau der alten Engine am 06.08. Jeder Lauf legt eine eigene
  // Variante an (die „june_pipeline"-Zeilen in der Tabelle sind genau
  // das). Sinnvoll, weil der GA stochastisch ist: derselbe Entwurf kann
  // im nächsten Anlauf lösen.
  //
  // Der Aufruf blockiert bis zum Ende des Laufs (Minuten) — dieselbe
  // Mechanik wie der Submit im Zeichner. Solange läuft ein Timer und
  // die Knöpfe sind gesperrt, damit nicht versehentlich zwei GA-Läufe
  // gleichzeitig auf derselben Maschine starten.
  const [recalcSeconds, setRecalcSeconds] = useState(null);   // null = läuft nicht
  const recalcTimerRef = useRef(null);
  const isRecalculating = recalcSeconds !== null;

  // Timer sauber abräumen, falls die Seite mitten im Lauf verlassen wird.
  useEffect(() => () => {
    if (recalcTimerRef.current) clearInterval(recalcTimerRef.current);
  }, []);

  const fmtElapsed = (secs) =>
    `${String(Math.floor(secs / 60)).padStart(2, "0")}:${String(secs % 60).padStart(2, "0")}`;

  const handleRecalculate = async () => {
    if (isRecalculating) return;
    if (!window.confirm(
      "Neue Berechnung mit der gespeicherten Skizze starten?\n\n" +
      "Das dauert einige Minuten. Die bestehenden Varianten bleiben erhalten."
    )) return;

    setRecalcSeconds(0);
    recalcTimerRef.current = setInterval(() => setRecalcSeconds((s) => (s ?? 0) + 1), 1000);
    try {
      const res = await fetch(`${API_BASE}/reoptimize/${projectId}`, { method: "POST" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // Achtung: /reoptimize meldet Fehler mit HTTP 200 und
      // {"status": "error", ...} — ohne diese Prüfung würde ein
      // abgebrochener Lauf hier als Erfolg durchgehen.
      const data = await res.json().catch(() => ({}));
      if (data.status === "error") throw new Error(data.detail || "Unbekannter Fehler");
      await reload();
      showToast("Neue Variante berechnet.", 5000);
    } catch (e) {
      console.error("Recalculate failed", e);
      showToast(`Neuberechnung fehlgeschlagen: ${e.message}`, 6000);
    } finally {
      if (recalcTimerRef.current) clearInterval(recalcTimerRef.current);
      recalcTimerRef.current = null;
      setRecalcSeconds(null);
    }
  };

  // ── Split into bestanden / nicht bestanden (QW-2) ───────────────────
  // The backend now flags each calc `passed` (EN 12193 Em/Uo/RG) and
  // derives `is_recommended` = best PASSING variant by uniformity. We show
  // passing variants in the main table and tuck the failed ones into a
  // collapsed section (Rainer 2026-05-29: "Varianten, die nicht geklappt
  // haben, hier nicht zeigen"). Defensive fallback to the metric thresholds
  // if an older backend hasn't sent `passed` yet.
  const isPassed = (c) =>
    c.passed === true ||
    (c.passed === undefined &&
      (c.metrics?.pa_ehave ?? 0) >= 75 &&
      (c.metrics?.pa_u ?? 0) >= 0.5 &&
      (c.metrics?.rg ?? 999) <= 55);

  // Sort a set of rows: recommended pinned to the top, then by active sort.
  const sortRows = (rows) => {
    const pickValue = (row) => {
      switch (sort.key) {
        case "best_fitness":  return row.best_fitness ?? -Infinity;
        case "total_wattage": return row.total_wattage ?? Infinity;  // lower is better
        case "pa_ehave":      return row.metrics?.pa_ehave ?? -Infinity;
        case "pa_u":          return row.metrics?.pa_u ?? -Infinity;
        case "rg":            return row.metrics?.rg ?? Infinity;    // lower is better
        // Zeichnungsdaten (2026-08-11): Zählwerte aus dem Geometrie-Abzug
        // je Lauf; die Layout-Spalte sortiert nach der Mastzahl.
        case "masts":         return row.drawing?.masts ?? -Infinity;
        case "area":          return row.drawing?.area ?? -Infinity;
        case "luminaires":    return row.drawing?.luminaires ?? -Infinity;
        case "facades":       return row.drawing?.facades ?? -Infinity;
        case "created_at":    return row.created_at || "";
        default:              return 0;
      }
    };
    const dirFactor = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      if (a.is_recommended && !b.is_recommended) return -1;
      if (!a.is_recommended && b.is_recommended) return 1;
      const av = pickValue(a);
      const bv = pickValue(b);
      if (typeof av === "string") return av.localeCompare(bv) * dirFactor;
      return (av - bv) * dirFactor;
    });
  };

  const passedRows = useMemo(
    () => sortRows(calculations.filter(isPassed)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [calculations, sort]
  );
  const failedRows = useMemo(
    () =>
      [...calculations.filter((c) => !isPassed(c))].sort((a, b) =>
        (b.created_at || "").localeCompare(a.created_at || "")
      ),
    [calculations]
  );
  // No variant meets the norm — show a warning instead of an empty table.
  const nonePassed = calculations.length > 0 && passedRows.length === 0;

  const onHeader = (key) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: key === "rg" || key === "total_wattage" ? "asc" : "desc" }
    );
  };
  const sortMarker = (key) => (sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : "");

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("de-DE", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    } catch (_) { return iso; }
  };

  // 2026-09-21 (Rainers Screenshots): Die 7-Kriterien-Fitness des Aug-26-
  // Drops meldet eine geloeste Anlage mit dem Sentinel 1e6 — in der Tabelle
  // stand dann ueberall "1000000". Ab dem Sentinel zeigen wir ein
  // "bestanden"-Badge; kleinere Werte (Alt-Laeufe, ungeloest) bleiben Zahl.
  const FITNESS_SENTINEL = 1e6;
  const fmtFitness = (f) => {
    if (f == null) return "—";
    if (f >= FITNESS_SENTINEL) {
      return (
        <span
          title="Alle Kriterien der Norm erfüllt (Fitness-Sentinel der 7-Kriterien-Bewertung)"
          style={{
            display: "inline-block",
            padding: "1px 8px",
            borderRadius: 999,
            background: "var(--signify-green, #00e487)",
            color: "#0b1b12",
            fontSize: "0.75rem",
            fontWeight: 700,
            whiteSpace: "nowrap",
          }}
        >
          ✓ erfüllt
        </span>
      );
    }
    return f;
  };

  const fmtNum = (n, digits = 0) => {
    if (n === null || n === undefined) return "—";
    if (digits === 0) return Math.round(n).toLocaleString("de-DE");
    return Number(n).toFixed(digits);
  };

  // One table row — reused for passing rows and the muted failed section.
  // `failed` rows are dimmed, get a "nicht bestanden" pill and no ★ promote.
  const renderTableRow = (c, { failed = false } = {}) => (
    <tr
      key={c.id}
      className={c.is_recommended ? "is-recommended" : ""}
      style={failed ? { opacity: 0.6 } : undefined}
    >
      <td>
        {c.is_recommended && (
          <span className="badge-recommend" style={{ marginRight: 8 }}>★ Empfehlung</span>
        )}
        {failed && (
          <span className="pill pill-fail" style={{ marginRight: 8 }}>nicht bestanden</span>
        )}
        <span style={{ fontWeight: 600, ...pipelineLabelStyle(c.variant_label) }}>
          {c.variant_label}
        </span>
      </td>
      <td style={{ color: "var(--text-muted)", fontSize: "var(--type-xs)" }}>
        {fmtDate(c.created_at)}
      </td>
      {/* Zeichnungsdaten der Variante (Geometrie-Abzug je Lauf, 0014).
          Layout = abgeleiteter Preset-Name (Backend, via symmetry-Signatur),
          sonst die Mastzahl. */}
      <td
        style={{ whiteSpace: "nowrap" }}
        title={c.drawing?.layout ?? undefined}
      >
        {c.drawing?.layout ?? (c.drawing?.masts != null ? `${c.drawing.masts} Masten` : "—")}
      </td>
      {/* Feldfläche in m² aus dem Geometrie-Abzug dieses Laufs */}
      <td className="num">{fmtNum(c.drawing?.area)}</td>
      <td className="num">{c.drawing?.luminaires ?? "—"}</td>
      <td className="num">{c.drawing?.facades ?? "—"}</td>
      <td className="num" style={{ fontWeight: 700, color: "var(--text-strong)" }}>
        {fmtFitness(c.best_fitness)}
      </td>
      <td className="num">{c.metrics?.pa_ehave ?? "—"}</td>
      <td className="num">{c.metrics?.pa_u ?? "—"}</td>
      <td className="num">{c.metrics?.rg ?? "—"}</td>
      <td className="num">{fmtNum(c.total_wattage)}</td>
      <td className="actions">
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => openReport(c.id)}
          title="Report dieser Berechnung öffnen"
        >Report</button>
        {!c.is_recommended && !failed && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => handleRecommend(c)}
            title="Diese Berechnung als Empfehlung markieren"
            aria-label="Als Empfehlung setzen"
          >★</button>
        )}
        {!c.is_recommended && calculations.length > 1 && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => handleDelete(c)}
            title="Löschen"
            aria-label="Löschen"
          >✕</button>
        )}
      </td>
    </tr>
  );

  // One KPI card — reused for passing cards and the muted failed section.
  const renderCard = (c, { failed = false } = {}) => (
    <div
      key={c.id}
      className={`kpi-card${c.is_recommended ? " is-recommended" : ""}`}
      style={failed ? { opacity: 0.7 } : undefined}
    >
      <div className="kpi-card-head">
        <div className="kpi-card-title" style={pipelineLabelStyle(c.variant_label)}>
          {c.variant_label}
        </div>
        {c.is_recommended && <span className="badge-recommend">★ Empfehlung</span>}
        {failed && <span className="pill pill-fail">nicht bestanden</span>}
      </div>

      <div className="kpi-card-meta">{fmtDate(c.created_at)}</div>

      <div className="kpi-row">
        <span className="label">Fitness</span>
        <span className="value">
          {fmtFitness(c.best_fitness)}
          {/* "/ 700" nur fuer die alte Zahlenskala, nicht neben dem Badge */}
          {c.best_fitness != null && c.best_fitness < FITNESS_SENTINEL && (
            <span style={{ color: "var(--text-muted)", fontWeight: 400, marginLeft: 4 }}>/ 700</span>
          )}
        </span>
      </div>

      {c.metrics && (
        <>
          <div className="kpi-row">
            <span className="label">Ē (Em)</span>
            <span className="value">{c.metrics.pa_ehave ?? "—"} lx</span>
          </div>
          <div className="kpi-row">
            <span className="label">U</span>
            <span className="value">{c.metrics.pa_u ?? "—"}</span>
          </div>
          <div className="kpi-row">
            <span className="label">RG</span>
            <span className="value">{c.metrics.rg ?? "—"}</span>
          </div>
        </>
      )}

      {c.total_wattage !== null && c.total_wattage !== undefined && (
        <div className="kpi-row">
          <span className="label">Wattage</span>
          <span className="value">{fmtNum(c.total_wattage)} W</span>
        </div>
      )}

      {/* Zeichnungsdaten der Variante (2026-08-11): Layout-Name aus dem
          Geometrie-Abzug plus Leuchten/Fassaden kompakt darunter. */}
      {c.drawing && (
        <>
          <div className="kpi-row" title="Mastlayout dieser Berechnung — Preset, sonst Mastzahl">
            <span className="label">Layout</span>
            <span className="value">
              {c.drawing.layout ?? (c.drawing.masts != null ? `${c.drawing.masts} Masten` : "—")}
            </span>
          </div>
          <div className="kpi-row" title="Feldfläche (m²) · Leuchten gesamt · Fassaden (Anwohner-Gebäude)">
            <span className="label">Zeichnung</span>
            <span className="value">
              {fmtNum(c.drawing.area)} m² · {c.drawing.luminaires ?? "—"} Leuchten · {c.drawing.facades ?? "—"} Fassaden
            </span>
          </div>
        </>
      )}

      <div className="kpi-card-actions">
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => openReport(c.id)}
          style={{ flex: 1 }}
        >Report</button>
        {!c.is_recommended && !failed && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => handleRecommend(c)}
            style={{ flex: 1 }}
          >★ Empfehlung</button>
        )}
        {!c.is_recommended && calculations.length > 1 && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => handleDelete(c)}
            title="Löschen"
            aria-label="Löschen"
          >✕</button>
        )}
      </div>
    </div>
  );

  // Spaltenbreiten fuer beide Tabellen: .data-table hat table-layout:fixed,
  // ohne <colgroup> bekaeme jede der 12 Spalten dasselbe Zwoelftel und die
  // Layout-Spalte ("6 Masten · 1·2·1") wuerde per Ellipsis abgeschnitten.
  const tableColgroup = (
    <colgroup>
      <col style={{ width: "15%" }} /> {/* Variante */}
      <col style={{ width: "10%" }} /> {/* Datum */}
      <col style={{ width: "12%" }} /> {/* Layout */}
      <col style={{ width: "7%" }} />  {/* Fläche */}
      <col style={{ width: "6%" }} />  {/* Leuchten */}
      <col style={{ width: "6%" }} />  {/* Fassaden */}
      <col style={{ width: "8%" }} />  {/* Fitness */}
      <col style={{ width: "7%" }} />  {/* Ē */}
      <col style={{ width: "5%" }} />  {/* U */}
      <col style={{ width: "5%" }} />  {/* RG */}
      <col style={{ width: "9%" }} />  {/* W */}
      <col style={{ width: "10%" }} /> {/* Aktionen */}
    </colgroup>
  );

  // Collapsible "Nicht bestanden (N)" toggle — shared by both views.
  const failedToggle = failedRows.length > 0 && (
    <button
      type="button"
      className="btn btn-ghost btn-sm"
      onClick={() => setShowFailed((s) => !s)}
      aria-expanded={showFailed || nonePassed}
      style={{ marginTop: "var(--space-3)" }}
    >
      {showFailed || nonePassed ? "▾" : "▸"} Nicht bestanden ({failedRows.length})
    </button>
  );

  return (
    <div style={{
      // Full-bleed overlay only when invoked legacy-style (Topbar prop);
      // when mounted as a Route the parent <div style="overflow:auto">
      // already provides the scroll context.
      ...(onBackProp ? {
        position: "fixed", inset: 0,
        background: "var(--bg-page)",
        zIndex: 1500,
        display: "flex", flexDirection: "column",
      } : {}),
    }}>
      {/* ── Header: project title + metadata pills + actions ──────── */}
      <div style={{
        padding: "12px 20px",
        borderBottom: "1px solid var(--border-default)",
        display: "flex", alignItems: "flex-start", justifyContent: "space-between",
        gap: 12,
        flexShrink: 0,
        background: "var(--surface-card)",
      }}>
        <div>
          <h1 style={{
            fontSize: 18, margin: 0,
            letterSpacing: "var(--tracking-tight)",
            color: "var(--text-strong)",
          }}>
            {project?.project_name || `Projekt ${projectId}`}
          </h1>
          <div style={{
            fontSize: 12,
            color: "var(--text-muted)",
            marginTop: 3,
            fontFamily: "var(--font-mono)",
          }}>
            {project?.project_number ? `Nr. ${project.project_number} · ` : ""}
            {project?.project_address || ""}
            {project?.project_town ? `, ${project.project_town}` : ""}
            {project?.country ? ` · ${project.country}` : ""}
          </div>
          {/* Type + priority pills — hidden when empty so legacy
              projects (pre-0008) don't show "—" placeholders. */}
          <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
            {project?.project_type && (
              <span className="pill pill-info">{project.project_type}</span>
            )}
            {project?.priority === "urgent" && (
              <span className="pill pill-fail">Dringend</span>
            )}
            {project?.priority === "high" && (
              <span className="pill pill-warn">Hoch</span>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {/* Re-sketch + edit-metadata shortcuts only on the routed page
              (not when mounted as the legacy Topbar overlay). */}
          {!onBackProp && (
            <>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => navigate(`/projects/${projectId}/edit`)}
                title="Metadaten bearbeiten"
                disabled={isRecalculating}
              >Metadaten</button>
              {/* Drei Stufen, von „nichts anfassen" bis „von vorn":
                  Neu berechnen → gleiche Skizze, neuer GA-Lauf
                  Skizze bearbeiten → gespeicherte Zeichnung korrigieren
                  Neu skizzieren → leere Karte, alles neu zeichnen
                  Nur sinnvoll, sobald überhaupt eine Skizze existiert —
                  das ist genau dann der Fall, wenn es Berechnungen gibt. */}
              {calculations.length > 0 && (
                <>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={handleRecalculate}
                    disabled={isRecalculating}
                    title="Gespeicherte Skizze erneut rechnen — ergibt eine weitere Variante"
                  >
                    {isRecalculating ? `Rechnet… ${fmtElapsed(recalcSeconds)}` : "Neu berechnen"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={openEditor}
                    disabled={isRecalculating}
                    title="Gespeicherte Skizze laden, anpassen und neu rechnen"
                  >Skizze bearbeiten</button>
                </>
              )}
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => navigate(`/projects/${projectId}/sketch`)}
                title="Von vorn zeichnen — verwirft die gespeicherte Geometrie beim nächsten Rechnen"
                disabled={isRecalculating}
              >Neu skizzieren</button>
            </>
          )}
          <button type="button" className="btn btn-ghost btn-sm" onClick={onBack}>
            ← Zurück
          </button>
        </div>
      </div>

      {/* ── Body ──────────────────────────────────────────────────── */}
      <div style={{
        padding: "14px 20px",
        overflowY: "auto",
        flexGrow: 1,
      }}>
        {/* Laufender GA — der Aufruf blockiert minutenlang, ohne diesen
            Hinweis sähe die Seite einfach nur eingefroren aus. Farben
            inline statt einer banner-info-Klasse: in App.css gibt es nur
            banner-fail / banner-warn, und „läuft" ist keins von beidem.
            Gleiche Blautöne wie das Ergebnis-Panel im Zeichner. */}
        {isRecalculating && (
          <div
            className="banner"
            style={{
              marginBottom: "var(--space-3)",
              background: "#f0f9ff",
              border: "1px solid #bae6fd",
              color: "#0369a1",
            }}
          >
            Neue Berechnung läuft … {fmtElapsed(recalcSeconds)} — Seite offen lassen.
            Die neue Variante erscheint hier, sobald der Lauf fertig ist.
          </div>
        )}
        {loading && (
          <div className="empty-state">Lade Berechnungen…</div>
        )}
        {error && (
          <div className="banner banner-fail">Fehler beim Laden: {error}</div>
        )}
        {!loading && !error && calculations.length === 0 && (
          <div className="empty-state">
            Noch keine Berechnungen für dieses Projekt.
          </div>
        )}

        {/* View toggle: side-by-side comparison vs. browse-cards */}
        {calculations.length > 0 && (
          <div style={{
            display: "flex", justifyContent: "space-between",
            alignItems: "center", marginBottom: "var(--space-4)",
          }}>
            <div className="section-title" style={{ marginBottom: 0 }}>
              <h2 style={{ fontSize: "var(--type-lg)" }}>Berechnungen</h2>
              <div className="subtitle">
                {calculations.length} {calculations.length === 1 ? "Variante" : "Varianten"} ·
                {" "}{passedRows.length} bestanden · Empfehlung = beste Gleichmäßigkeit
              </div>
            </div>
            <div className="view-toggle" role="tablist" aria-label="Ansicht">
              <button
                type="button"
                role="tab"
                aria-selected={view === "table"}
                className={view === "table" ? "is-active" : ""}
                onClick={() => setView("table")}
              >Tabelle</button>
              <button
                type="button"
                role="tab"
                aria-selected={view === "cards"}
                className={view === "cards" ? "is-active" : ""}
                onClick={() => setView("cards")}
              >Karten</button>
            </div>
          </div>
        )}

        {/* ── Comparison table view ──────────────────────────────────
            Recommended row pinned to the top regardless of sort dir.
            Numeric columns use .num (mono + tabular nums) so values
            line up perfectly across rows for visual comparison. */}
        {calculations.length > 0 && view === "table" && (
          <>
            {nonePassed && (
              <div className="banner banner-warn" style={{ marginBottom: "var(--space-3)" }}>
                Keine Variante erfüllt die EN-12193-Vorgaben (Em ≥ 75 lx, Uo ≥ 0,5,
                RG ≤ 55). Alle {failedRows.length} Läufe stehen unten unter
                „Nicht bestanden".
              </div>
            )}
            {passedRows.length > 0 && (
              <div className="report-card">
                <div className="report-card-body flush">
                  <table className="data-table">
                    {tableColgroup}
                    <thead>
                      <tr>
                        <th>Variante</th>
                        <th className="sortable" onClick={() => onHeader("created_at")}>
                          Datum{sortMarker("created_at")}
                        </th>
                        {/* Zeichnungsdaten je Variante (2026-08-11) */}
                        <th className="sortable" onClick={() => onHeader("masts")}
                            title="Mastlayout dieser Berechnung — Preset (z. B. 2·2·2 = je 2 Leuchten pro Mast), sonst Mastzahl; sortiert nach Mastzahl">
                          Layout{sortMarker("masts")}
                        </th>
                        <th className="sortable num" onClick={() => onHeader("area")}
                            title="Feldfläche in m² aus dem Geometrie-Abzug dieser Berechnung">
                          m²{sortMarker("area")}
                        </th>
                        <th className="sortable num" onClick={() => onHeader("luminaires")}
                            title="Anzahl Leuchten gesamt (Lichtpunkte der Zeichnung)">
                          Leuchten{sortMarker("luminaires")}
                        </th>
                        <th className="sortable num" onClick={() => onHeader("facades")}
                            title="Anzahl Fassaden (Anwohner-Gebäude) in der Zeichnung">
                          Fassaden{sortMarker("facades")}
                        </th>
                        <th className="sortable num" onClick={() => onHeader("best_fitness")}
                            title="Bewertung des Optimierers; ✓ erfüllt = alle Norm-Kriterien erreicht">
                          Fitness{sortMarker("best_fitness")}
                        </th>
                        <th className="sortable num" onClick={() => onHeader("pa_ehave")}>
                          Ē (lx){sortMarker("pa_ehave")}
                        </th>
                        <th className="sortable num" onClick={() => onHeader("pa_u")}>
                          U{sortMarker("pa_u")}
                        </th>
                        <th className="sortable num" onClick={() => onHeader("rg")}>
                          RG{sortMarker("rg")}
                        </th>
                        <th className="sortable num" onClick={() => onHeader("total_wattage")}>
                          W (Gesamt){sortMarker("total_wattage")}
                        </th>
                        <th style={{ textAlign: "right" }}>Aktionen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {passedRows.map((c) => renderTableRow(c))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── Nicht bestanden (eingeklappt, QW-2) ───────────────── */}
            {failedToggle}
            {(showFailed || nonePassed) && failedRows.length > 0 && (
              <div className="report-card" style={{ marginTop: "var(--space-2)" }}>
                <div className="report-card-body flush">
                  <table className="data-table">
                    {tableColgroup}
                    <thead>
                      <tr>
                        <th>Variante</th>
                        <th>Datum</th>
                        {/* Zeichnungsdaten je Variante (2026-08-11) */}
                        <th title="Mastlayout dieser Berechnung — Preset, sonst Mastzahl">Layout</th>
                        <th className="num" title="Feldfläche in m² aus dem Geometrie-Abzug dieser Berechnung">m²</th>
                        <th className="num" title="Anzahl Leuchten gesamt (Lichtpunkte der Zeichnung)">Leuchten</th>
                        <th className="num" title="Anzahl Fassaden (Anwohner-Gebäude) in der Zeichnung">Fassaden</th>
                        <th className="num">Fitness</th>
                        <th className="num">Ē (lx)</th>
                        <th className="num">U</th>
                        <th className="num">RG</th>
                        <th className="num">W (Gesamt)</th>
                        <th style={{ textAlign: "right" }}>Aktionen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {failedRows.map((c) => renderTableRow(c, { failed: true }))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── Cards view — one KPI card per Calculation variant ───── */}
        {calculations.length > 0 && view === "cards" && (
          <>
            {nonePassed && (
              <div className="banner banner-warn" style={{ marginBottom: "var(--space-3)" }}>
                Keine Variante erfüllt die EN-12193-Vorgaben (Em ≥ 75 lx, Uo ≥ 0,5,
                RG ≤ 55). Alle {failedRows.length} Läufe stehen unten unter
                „Nicht bestanden".
              </div>
            )}
            {passedRows.length > 0 && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: "var(--space-4)",
              }}>
                {passedRows.map((c) => renderCard(c))}
              </div>
            )}

            {/* ── Nicht bestanden (eingeklappt, QW-2) ───────────────── */}
            {failedToggle}
            {(showFailed || nonePassed) && failedRows.length > 0 && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: "var(--space-4)",
                marginTop: "var(--space-2)",
              }}>
                {failedRows.map((c) => renderCard(c, { failed: true }))}
              </div>
            )}
          </>
        )}
      </div>

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
};

export default ProjectDetailPage;
